import dayjs from 'dayjs';

import DATEFORMAT from '../../utils/date-format';
import {
  ApiDataPoint,
  ApiItem,
  ChartDataPoint,
  IWidget,
  WidgetComparable,
  WidgetLayout,
} from '../types/scenarioAnalysis.types';

/**
 * The set of widget fields that count towards "unsaved changes" detection.
 * Keeping this list in one place ensures the comparison can't silently drift
 * from `IWidget` (every persisted/editable field must be represented here).
 */
export const COMPARABLE_KEYS = [
  'id',
  'x',
  'y',
  'width',
  'height',
  'zIndex',
  'tableView',
  'chartType',
  'dateRange',
  'Allocations',
  'Nominations',
  'DeadlineNominations',
  'Forecast_Weekly',
  'Forecast_Monthly',
  'Forecast_Yearly',
  'Forecast_Quarterly',
  'Forecast18Month',
  'Forecast24Month',
  'Forecast2Year',
  'Forecast3Year',
  'Forecast4Year',
  'Forecast5Year',
  'ForecastFullDeal',
  'fieldSelection',
  'fieldId',
  'deliveryPointId',
  'volumeType',
  'selectedReportTypeIds',
] as const satisfies readonly (keyof WidgetComparable)[];

/** Reduce a list of widgets to only the fields relevant for change detection. */
export const normalizeWidgets = (widgets: IWidget[]): WidgetComparable[] =>
  widgets.map((widget) =>
    COMPARABLE_KEYS.reduce((acc, key) => {
      (acc as Record<string, unknown>)[key] = widget[key];
      return acc;
    }, {} as WidgetComparable),
  );

/** Merge each widget with its stored layout (position/size/z-index). */
export const mergeWidgetsWithLayouts = (
  widgets: IWidget[],
  layouts: Record<string, WidgetLayout>,
): IWidget[] =>
  widgets.map((widget) => {
    const layout = layouts[widget.id];
    return layout ? { ...widget, ...layout } : widget;
  });

export const processChartDataPoint = (
  dataPoint: ApiDataPoint,
  item: ApiItem,
  dataMap: Map<string, ChartDataPoint>,
  seriesUomObj: Record<string, string | null>,
): Record<string, string | null> => {
  const dateKey = dayjs(dataPoint.gasDay).format(DATEFORMAT.yearMonthDay);

  if (!dataMap.has(dateKey)) {
    dataMap.set(dateKey, {
      gasDay: dayjs(dataPoint.gasDay).format(DATEFORMAT.dayMonthYear),
      date: dataPoint.gasDay,
      ...(dataPoint.deliveryPoint && {
        deliveryPoint: dataPoint.deliveryPoint,
      }),
      ...(dataPoint.field && { field: dataPoint.field }),
    });
  }

  const existing = dataMap.get(dateKey)!;
  const seriesKey = item.forecastType ? `${item.forecastType}` : item.type;

  existing[seriesKey] = dataPoint.volume;
  existing.uom = item.uom;

  if (dataPoint.deliveryPoint) {
    existing.deliveryPoint = dataPoint.deliveryPoint;
  }
  if (dataPoint.field) {
    existing.field = dataPoint.field;
  }

  if (!seriesUomObj[seriesKey] && item.uom) {
    return { ...seriesUomObj, [seriesKey]: item.uom };
  }

  return seriesUomObj;
};

export const processWidgetData = (data: ApiItem[]) => {
  const points = data.flatMap((item) => (item.chartData ?? []).map((dp) => ({ item, dp })));

  const { dataMap, seriesUomObj } = points.reduce(
    (acc, { item, dp }) => {
      const updatedSeriesUomObj = processChartDataPoint(dp, item, acc.dataMap, acc.seriesUomObj);
      return { ...acc, seriesUomObj: updatedSeriesUomObj };
    },
    {
      dataMap: new Map<string, ChartDataPoint>(),
      seriesUomObj: {} as Record<string, string | null>,
    },
  );

  const chartData = Array.from(dataMap.values()).sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );

  const seriesKeys = Array.from(
    new Set(data.map((r) => (r.forecastType ? `${r.forecastType}` : r.type))),
  );

  return { chartData, seriesKeys, seriesUomObj };
};
